import { useCallback, useEffect, useState } from "react";
import {
  approveOrder,
  bulkShipOrders,
  cancelOrder,
  completeOrder,
  downloadShippingCsv,
  fetchAdminOrder,
  fetchAdminOrders,
  fetchTrackingCarriers,
  updateTracking,
  type AdminOrder,
  type AdminOrderQuery,
  type BulkShipResult,
  type TrackingCarrier,
} from "@/api/admin";
import { apiErrorMessage } from "@/api/client";
import type { OrderStatus, Pagination } from "@/api/types";
import { StatusBadge } from "@/components/StatusBadge";
import { formatDateTime, formatYen } from "@/utils/format";

const STATUS_OPTIONS: { value: OrderStatus | ""; label: string }[] = [
  { value: "", label: "すべて" },
  { value: "pending_approval", label: "承認待ち" },
  { value: "approved", label: "承認済み" },
  { value: "shipping", label: "出荷中" },
  { value: "completed", label: "完了" },
  { value: "cancelled", label: "キャンセル" },
];

export function AdminOrdersPage() {
  const [orders, setOrders] = useState<AdminOrder[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    per_page: 20,
    total: 0,
    total_pages: 0,
  });
  const [filters, setFilters] = useState<AdminOrderQuery>({ page: 1, per_page: 20 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<{ type: "ok" | "err"; text: string } | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [selected, setSelected] = useState<AdminOrder | null>(null);
  const [selectedLoading, setSelectedLoading] = useState(false);
  const [selectedError, setSelectedError] = useState<string | null>(null);
  const [downloadingCsv, setDownloadingCsv] = useState(false);
  // 一括出荷モーダル
  const [checkedIds, setCheckedIds] = useState<Set<number>>(new Set());
  const [bulkShipOpen, setBulkShipOpen] = useState(false);
  const [bulkShipping, setBulkShipping] = useState(false);
  const [bulkShipResult, setBulkShipResult] = useState<BulkShipResult | null>(null);
  // 追跡情報編集モーダル（既に出荷済のもの）
  const [editTrackingOrder, setEditTrackingOrder] = useState<AdminOrder | null>(null);
  // 配送業者リスト（dropdown 用）
  const [carriers, setCarriers] = useState<TrackingCarrier[]>([]);
  useEffect(() => {
    void (async () => {
      try {
        setCarriers(await fetchTrackingCarriers());
      } catch {
        // フォールバック: ハードコード
        setCarriers([
          { value: "yamato", label: "ヤマト運輸", has_tracking_url: true },
          { value: "sagawa", label: "佐川急便", has_tracking_url: true },
          { value: "japanpost", label: "日本郵便", has_tracking_url: true },
          { value: "seino", label: "西濃運輸", has_tracking_url: true },
          { value: "fukuyama", label: "福山通運", has_tracking_url: true },
          { value: "other", label: "その他", has_tracking_url: false },
        ]);
      }
    })();
  }, []);

  const onDownloadShippingCsv = async () => {
    if (
      !window.confirm(
        "承認済みの発注を対象にした出庫指示 CSV（Wonect 21 列）をダウンロードします。よろしいですか？",
      )
    )
      return;
    setDownloadingCsv(true);
    setFlash(null);
    try {
      const { blob, filename } = await downloadShippingCsv({ status: "approved" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setFlash({ type: "ok", text: `出庫指示 CSV をダウンロードしました: ${filename}` });
    } catch (e) {
      setFlash({
        type: "err",
        text: apiErrorMessage(e, "出庫指示 CSV のダウンロードに失敗しました"),
      });
    } finally {
      setDownloadingCsv(false);
    }
  };

  const toggleDetail = useCallback(
    async (order: AdminOrder) => {
      // 同じ行をクリックしたら閉じる
      if (selected?.id === order.id) {
        setSelected(null);
        setSelectedError(null);
        return;
      }
      // 一覧レスポンスには items が含まれないので詳細を取り直す
      setSelected(order);
      setSelectedError(null);
      setSelectedLoading(true);
      try {
        const detail = await fetchAdminOrder(order.id);
        // setSelected を呼ぶ時点で、ユーザが別行に切り替えていないかチェック
        setSelected((current) => (current?.id === order.id ? detail : current));
      } catch (e) {
        setSelectedError(apiErrorMessage(e, "発注詳細の取得に失敗しました"));
      } finally {
        setSelectedLoading(false);
      }
    },
    [selected?.id],
  );

  const load = useCallback(
    async (q: AdminOrderQuery) => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchAdminOrders(q);
        setOrders(res.orders);
        setPagination(res.pagination);
      } catch (e) {
        setError(apiErrorMessage(e, "発注一覧の取得に失敗しました"));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    void load(filters);
  }, [filters, load]);

  const runAction = async (
    order: AdminOrder,
    action: "approve" | "cancel" | "ship" | "complete",
  ) => {
    const labels = {
      approve: "承認（出庫CSV生成）",
      cancel: "キャンセル（返金）",
      ship: "出荷開始",
      complete: "完了",
    };
    if (!window.confirm(`発注 ${order.order_number} を「${labels[action]}」しますか？`)) return;

    setBusyId(order.id);
    setFlash(null);
    try {
      if (action === "approve") {
        const res = await approveOrder(order.id);
        setFlash({
          type: "ok",
          text: `承認しました。CSV: ${res.export_csv_path}`,
        });
      } else if (action === "cancel") {
        await cancelOrder(order.id);
        setFlash({ type: "ok", text: `キャンセルしました（返金処理済み）` });
      } else if (action === "ship") {
        // 単発出荷も追跡情報必須のため、モーダルを開く
        setCheckedIds(new Set([order.id]));
        setBulkShipOpen(true);
        setBusyId(null);
        return;
      } else if (action === "complete") {
        await completeOrder(order.id);
        setFlash({ type: "ok", text: `完了にしました` });
      }
      await load(filters);
      if (selected && selected.id === order.id) setSelected(null);
    } catch (e) {
      setFlash({ type: "err", text: apiErrorMessage(e, "処理に失敗しました") });
    } finally {
      setBusyId(null);
    }
  };

  const approvedOrders = orders.filter((o) => o.status === "approved");
  const checkedApproved = approvedOrders.filter((o) => checkedIds.has(o.id));
  const onToggleAll = () => {
    if (checkedApproved.length === approvedOrders.length && approvedOrders.length > 0) {
      // 全選択中 → 全解除
      setCheckedIds((prev) => {
        const next = new Set(prev);
        approvedOrders.forEach((o) => next.delete(o.id));
        return next;
      });
    } else {
      setCheckedIds((prev) => {
        const next = new Set(prev);
        approvedOrders.forEach((o) => next.add(o.id));
        return next;
      });
    }
  };
  const onToggleOne = (id: number) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-semibold">発注管理</h1>
        <div className="flex gap-2 flex-wrap">
          {checkedIds.size > 0 && (
            <button
              type="button"
              className="btn-primary"
              onClick={() => setBulkShipOpen(true)}
            >
              📦 選択中 {checkedIds.size} 件を一括出荷
            </button>
          )}
          <button
            type="button"
            className="btn-secondary"
            onClick={() => void onDownloadShippingCsv()}
            disabled={downloadingCsv}
            title="承認済みの発注をまとめた Wonect 21 列 CSV をダウンロードします"
          >
            {downloadingCsv ? "作成中..." : "出庫指示 CSV 一括ダウンロード"}
          </button>
        </div>
      </div>

      <div className="card p-4 grid md:grid-cols-4 gap-3">
        <div>
          <label className="label" htmlFor="status">ステータス</label>
          <select
            id="status"
            className="input"
            value={filters.status ?? ""}
            onChange={(e) =>
              setFilters({ ...filters, page: 1, status: (e.target.value || "") as OrderStatus | "" })
            }
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="tenant_id">顧客ID</label>
          <input
            id="tenant_id"
            type="number"
            className="input"
            value={filters.tenant_id ?? ""}
            onChange={(e) =>
              setFilters({
                ...filters,
                page: 1,
                tenant_id: e.target.value ? Number(e.target.value) : undefined,
              })
            }
          />
        </div>
        <div>
          <label className="label" htmlFor="date_from">開始日</label>
          <input
            id="date_from"
            type="date"
            className="input"
            value={filters.date_from ?? ""}
            onChange={(e) =>
              setFilters({ ...filters, page: 1, date_from: e.target.value || undefined })
            }
          />
        </div>
        <div>
          <label className="label" htmlFor="date_to">終了日</label>
          <input
            id="date_to"
            type="date"
            className="input"
            value={filters.date_to ?? ""}
            onChange={(e) =>
              setFilters({ ...filters, page: 1, date_to: e.target.value || undefined })
            }
          />
        </div>
      </div>

      {flash && (
        <div
          className={`rounded-md px-4 py-3 text-sm border ${
            flash.type === "ok"
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          }`}
        >
          {flash.text}
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-500">読み込み中...</div>
      ) : orders.length === 0 ? (
        <div className="card p-8 text-center text-gray-500">該当する発注がありません。</div>
      ) : (
        <div className="card overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-3 py-2 text-center font-medium w-10">
                  {approvedOrders.length > 0 && (
                    <input
                      type="checkbox"
                      checked={
                        checkedApproved.length === approvedOrders.length &&
                        approvedOrders.length > 0
                      }
                      onChange={onToggleAll}
                      title="承認済みを全選択 / 全解除"
                    />
                  )}
                </th>
                <th className="px-3 py-2 text-left font-medium">発注番号</th>
                <th className="px-3 py-2 text-left font-medium">顧客</th>
                <th className="px-3 py-2 text-left font-medium">日時</th>
                <th className="px-3 py-2 text-right font-medium">合計</th>
                <th className="px-3 py-2 text-center font-medium">ステータス</th>
                <th className="px-3 py-2 font-medium">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {orders.map((o) => (
                <tr key={o.id} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-center">
                    {o.status === "approved" ? (
                      <input
                        type="checkbox"
                        checked={checkedIds.has(o.id)}
                        onChange={() => onToggleOne(o.id)}
                      />
                    ) : null}
                  </td>
                  <td className="px-3 py-2 font-medium">
                    <button
                      type="button"
                      className="text-brand-700 hover:underline"
                      onClick={() => void toggleDetail(o)}
                    >
                      {o.order_number}
                    </button>
                  </td>
                  <td className="px-3 py-2">
                    {o.tenant?.name} <span className="text-xs text-gray-500">({o.tenant?.plan})</span>
                  </td>
                  <td className="px-3 py-2 text-gray-600">{formatDateTime(o.created_at)}</td>
                  <td className="px-3 py-2 text-right">{formatYen(o.total_amount)}</td>
                  <td className="px-3 py-2 text-center">
                    <StatusBadge status={o.status} />
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1 flex-wrap">
                      {o.status === "pending_approval" && (
                        <>
                          <button
                            type="button"
                            className="btn-primary px-2 py-1 text-xs"
                            disabled={busyId === o.id}
                            onClick={() => void runAction(o, "approve")}
                          >
                            承認
                          </button>
                          <button
                            type="button"
                            className="btn-danger px-2 py-1 text-xs"
                            disabled={busyId === o.id}
                            onClick={() => void runAction(o, "cancel")}
                          >
                            キャンセル
                          </button>
                        </>
                      )}
                      {o.status === "approved" && (
                        <button
                          type="button"
                          className="btn-primary px-2 py-1 text-xs"
                          disabled={busyId === o.id}
                          onClick={() => void runAction(o, "ship")}
                        >
                          出荷開始
                        </button>
                      )}
                      {o.status === "shipping" && (
                        <button
                          type="button"
                          className="btn-primary px-2 py-1 text-xs"
                          disabled={busyId === o.id}
                          onClick={() => void runAction(o, "complete")}
                        >
                          完了
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <div className="card p-4 border-brand-500">
          <div className="flex justify-between items-start mb-3">
            <h2 className="font-semibold">
              {selected.order_number} の明細
              {selectedLoading && (
                <span className="ml-2 text-xs text-gray-500">読み込み中...</span>
              )}
            </h2>
            <button
              type="button"
              className="text-gray-500 hover:text-gray-700"
              onClick={() => setSelected(null)}
            >
              ✕
            </button>
          </div>
          <dl className="grid grid-cols-2 gap-2 text-sm mb-3">
            <dt className="text-gray-500">顧客</dt>
            <dd>{selected.tenant?.name}</dd>
            <dt className="text-gray-500">合計</dt>
            <dd>{formatYen(selected.total_amount)}</dd>
            <dt className="text-gray-500">発送先</dt>
            <dd>{selected.shipping_address || "-"}</dd>
            <dt className="text-gray-500">承認日時</dt>
            <dd>{formatDateTime(selected.approved_at)}</dd>
            <dt className="text-gray-500">出荷日時</dt>
            <dd>{formatDateTime(selected.shipped_at)}</dd>
            {(selected.status === "shipping" || selected.status === "completed") && (
              <>
                <dt className="text-gray-500">追跡情報</dt>
                <dd>
                  {selected.tracking_number ? (
                    <>
                      {(carriers.find((c) => c.value === selected.tracking_carrier)
                        ?.label) || selected.tracking_carrier}{" "}
                      <span className="font-mono">{selected.tracking_number}</span>
                      {selected.tracking_url && (
                        <>
                          {" "}
                          <a
                            href={selected.tracking_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-brand-700 underline text-xs"
                          >
                            追跡 →
                          </a>
                        </>
                      )}
                      <button
                        type="button"
                        className="btn-secondary px-2 py-0.5 text-xs ml-2"
                        onClick={() => setEditTrackingOrder(selected)}
                      >
                        編集
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn-secondary px-2 py-0.5 text-xs"
                      onClick={() => setEditTrackingOrder(selected)}
                    >
                      追跡番号を入力
                    </button>
                  )}
                </dd>
              </>
            )}
          </dl>
          {selectedError && (
            <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800 mb-2">
              {selectedError}
            </div>
          )}
          <table className="min-w-full text-sm border-t">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium">JAN</th>
                <th className="px-3 py-2 text-left font-medium">商品名</th>
                <th className="px-3 py-2 text-right font-medium">単価 (税込)</th>
                <th className="px-3 py-2 text-right font-medium">数量</th>
                <th className="px-3 py-2 text-right font-medium">小計 (税込)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {selected.items === undefined && !selectedLoading && !selectedError && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-gray-500 text-xs">
                    明細を取得できませんでした
                  </td>
                </tr>
              )}
              {selected.items && selected.items.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-gray-500 text-xs">
                    明細がありません
                  </td>
                </tr>
              )}
              {(selected.items ?? []).map((i) => (
                <tr key={i.id}>
                  <td className="px-3 py-2 font-mono text-xs text-gray-500">{i.jan}</td>
                  <td className="px-3 py-2">
                    {i.product_name ?? <span className="text-gray-400">（商品名不明）</span>}
                  </td>
                  <td className="px-3 py-2 text-right">{formatYen(i.tax_inclusive_unit_price)}</td>
                  <td className="px-3 py-2 text-right">{i.quantity}</td>
                  <td className="px-3 py-2 text-right">{formatYen(i.subtotal)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 border-t">
              <tr>
                <td colSpan={4} className="px-3 py-2 text-right text-gray-600">
                  合計 (税込)
                </td>
                <td className="px-3 py-2 text-right font-bold">
                  {formatYen(selected.total_amount)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => setFilters({ ...filters, page: Math.max(1, (filters.page ?? 1) - 1) })}
            disabled={(filters.page ?? 1) <= 1}
          >
            前へ
          </button>
          <div className="text-sm">
            {pagination.page} / {pagination.total_pages}（{pagination.total} 件）
          </div>
          <button
            type="button"
            className="btn-secondary"
            onClick={() =>
              setFilters({
                ...filters,
                page: Math.min(pagination.total_pages, (filters.page ?? 1) + 1),
              })
            }
            disabled={(filters.page ?? 1) >= pagination.total_pages}
          >
            次へ
          </button>
        </div>
      )}

      {bulkShipOpen && (
        <BulkShipDialog
          orderIds={Array.from(checkedIds)}
          ordersById={Object.fromEntries(orders.map((o) => [o.id, o]))}
          carriers={carriers}
          submitting={bulkShipping}
          result={bulkShipResult}
          onClose={() => {
            setBulkShipOpen(false);
            setBulkShipResult(null);
          }}
          onSubmit={async (carrier, number) => {
            setBulkShipping(true);
            try {
              const res = await bulkShipOrders({
                order_ids: Array.from(checkedIds),
                tracking_carrier: carrier,
                tracking_number: number,
              });
              setBulkShipResult(res);
              setFlash({
                type: "ok",
                text: `${res.shipped.length} 件を出荷しました${
                  res.skipped.length > 0 ? `（${res.skipped.length} 件スキップ）` : ""
                }`,
              });
              setCheckedIds(new Set());
              await load(filters);
            } catch (e) {
              setFlash({ type: "err", text: apiErrorMessage(e, "一括出荷に失敗しました") });
            } finally {
              setBulkShipping(false);
            }
          }}
        />
      )}

      {editTrackingOrder && (
        <EditTrackingDialog
          order={editTrackingOrder}
          carriers={carriers}
          onClose={() => setEditTrackingOrder(null)}
          onSubmit={async (carrier, number, resend) => {
            try {
              await updateTracking(editTrackingOrder.id, {
                tracking_carrier: carrier,
                tracking_number: number,
                resend_email: resend,
              });
              setFlash({
                type: "ok",
                text: resend ? "追跡情報を更新し再通知メールを送信しました" : "追跡情報を更新しました",
              });
              setEditTrackingOrder(null);
              await load(filters);
              if (selected && selected.id === editTrackingOrder.id) {
                const detail = await fetchAdminOrder(editTrackingOrder.id);
                setSelected(detail);
              }
            } catch (e) {
              setFlash({ type: "err", text: apiErrorMessage(e, "追跡情報の更新に失敗しました") });
            }
          }}
        />
      )}
    </div>
  );
}

// ---------- 一括出荷ダイアログ ----------

function BulkShipDialog({
  orderIds,
  ordersById,
  carriers,
  submitting,
  result,
  onClose,
  onSubmit,
}: {
  orderIds: number[];
  ordersById: Record<number, AdminOrder>;
  carriers: TrackingCarrier[];
  submitting: boolean;
  result: BulkShipResult | null;
  onClose: () => void;
  onSubmit: (carrier: string, number: string) => Promise<void>;
}) {
  const [carrier, setCarrier] = useState("yamato");
  const [number, setNumber] = useState("");

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="font-semibold text-lg">
            📦 一括出荷（{orderIds.length} 件）
          </h2>
          <button type="button" className="text-gray-500 hover:text-gray-700" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="p-4 space-y-4">
          {result ? (
            <div className="space-y-3">
              <div className="rounded-md bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800">
                ✅ {result.shipped.length} 件を出荷しました（追跡情報セット済 / 通知メール送信済）
              </div>
              {result.skipped.length > 0 && (
                <div className="rounded-md bg-yellow-50 border border-yellow-200 px-3 py-2 text-sm">
                  <div className="font-semibold mb-2 text-yellow-900">
                    ⚠️ スキップされた発注 {result.skipped.length} 件
                  </div>
                  <ul className="text-xs text-yellow-900 space-y-1">
                    {result.skipped.map((s) => (
                      <li key={s.order_id}>
                        ID {s.order_id}: {s.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="text-right">
                <button type="button" className="btn-primary" onClick={onClose}>
                  閉じる
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600">
                同日に発送する荷物は LOGEX 宛 1 個口で梱包するため、
                選択中の全発注に同じ追跡情報をセットします。
              </p>
              <div>
                <label className="label">配送業者</label>
                <select
                  className="input"
                  value={carrier}
                  onChange={(e) => setCarrier(e.target.value)}
                  disabled={submitting}
                >
                  {carriers.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                      {!c.has_tracking_url && " (追跡 URL なし)"}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label">追跡番号</label>
                <input
                  className="input"
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  placeholder="例: 1234-5678-9012"
                  disabled={submitting}
                />
              </div>
              <div className="border-t pt-3">
                <details>
                  <summary className="text-xs text-gray-500 cursor-pointer">
                    対象発注 {orderIds.length} 件を表示
                  </summary>
                  <ul className="text-xs mt-2 space-y-0.5 max-h-32 overflow-y-auto">
                    {orderIds.map((id) => {
                      const o = ordersById[id];
                      return (
                        <li key={id} className="font-mono">
                          {o?.order_number ?? `(id=${id})`} - {o?.tenant?.name ?? ""}
                        </li>
                      );
                    })}
                  </ul>
                </details>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={onClose}
                  disabled={submitting}
                >
                  キャンセル
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  disabled={submitting || !number.trim()}
                  onClick={() => void onSubmit(carrier, number.trim())}
                >
                  {submitting ? "処理中..." : "出荷確定 + 通知メール送信"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- 追跡情報編集ダイアログ ----------

function EditTrackingDialog({
  order,
  carriers,
  onClose,
  onSubmit,
}: {
  order: AdminOrder;
  carriers: TrackingCarrier[];
  onClose: () => void;
  onSubmit: (carrier: string, number: string, resend: boolean) => Promise<void>;
}) {
  const [carrier, setCarrier] = useState(order.tracking_carrier ?? "yamato");
  const [number, setNumber] = useState(order.tracking_number ?? "");
  const [resend, setResend] = useState(false);
  const [busy, setBusy] = useState(false);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="font-semibold">追跡情報の編集: {order.order_number}</h2>
          <button type="button" className="text-gray-500 hover:text-gray-700" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="label">配送業者</label>
            <select
              className="input"
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              disabled={busy}
            >
              {carriers.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">追跡番号</label>
            <input
              className="input"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              disabled={busy}
            />
          </div>
          <label className="label flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={resend}
              onChange={(e) => setResend(e.target.checked)}
              disabled={busy}
            />
            更新後に再通知メールを送信する
          </label>
          <div className="flex justify-end gap-2 pt-3 border-t">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
              キャンセル
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={busy || !number.trim()}
              onClick={async () => {
                setBusy(true);
                try {
                  await onSubmit(carrier, number.trim(), resend);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? "更新中..." : "更新"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
